import React, { useEffect, useMemo, useState } from "react";
import {
    Bell,
    ShieldAlert,
    Clock3,
    RefreshCcw,
    CheckCircle2,
    XCircle,
    Loader2,
    Filter,
    MessageSquareText,
} from "lucide-react";
import useNotification from "../../Sevices/useNotification";
import { useGlobalContext } from "../../GlobalContext";

function NotificationHistory() {
    const {
        getNotifications,
        updateDriverOffDutyRequest,
        historyLoading,
        historyActionLoadingId,
    } = useNotification();
    const { notificationRefreshKey, clearNotificationCount } = useGlobalContext();
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [refreshKey, setRefreshKey] = useState(0);

    const [actionModal, setActionModal] = useState({
        open: false,
        item: null,
        action: "",
        remarks: "",
    });

    const loadNotifications = async () => {
        const data = await getNotifications([statusFilter]);
        const normalized = (data || []).map((item, index) => {
            const isDriverOffDuty = !!item?.pk_notification_id;
            const isKyc = !!item?.kyc_id;

            return {
                id: item?.pk_notification_id || item?.kyc_id || index,
                type: isDriverOffDuty ? "off_duty" : isKyc ? "kyc" : "general",
                notificationId: item?.pk_notification_id || item?.kyc_id || null,
                title: item?.title || item?.kyc_title || "Notification",
                body: item?.body || item?.kyc_body || "No message available",
                createdOn: item?.createdOn || item?.kyc_created_on || null,
                raw: item,
            };
        });

        setItems(normalized);
    };
useEffect(() => {
  clearNotificationCount();
}, []);
useEffect(() => {
  loadNotifications();
}, [statusFilter, refreshKey, notificationRefreshKey]);

    const groupedItems = useMemo(() => {
        return items;
    }, [items]);

    const formatDateTime = (value) => {
        if (!value) return "—";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return new Intl.DateTimeFormat("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    };

    const extractDriverId = (text = "") => {
        const match = text.match(/\bPD\d+\b/i);
        return match ? match[0] : "";
    };

    const openActionModal = (item, action) => {
        setActionModal({
            open: true,
            item,
            action,
            remarks: "",
        });
    };

    const closeActionModal = () => {
        setActionModal({
            open: false,
            item: null,
            action: "",
            remarks: "",
        });
    };

    const handleSubmitAction = async () => {
        const currentItem = actionModal.item;
        if (!currentItem?.notificationId) return;

        await updateDriverOffDutyRequest({
            status: actionModal.action,
            driverId: extractDriverId(currentItem?.body),
            notificationId: currentItem.notificationId,
            adminRemarks: actionModal.remarks,
            onSuccess: async () => {
                closeActionModal();
                await loadNotifications();
            },
        });
    };

    const renderTypeIcon = (type) => {
        if (type === "off_duty") {
            return (
                <div className="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-orange-600" />
                </div>
            );
        }

        if (type === "kyc") {
            return (
                <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                </div>
            );
        }

        return (
            <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center">
                <MessageSquareText className="w-5 h-5 text-gray-600" />
            </div>
        );
    };

    return (
        <div className="w-full text-left">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">
                        Notification History
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        View all admin notifications and take action on off-duty requests.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-gray-200 bg-white">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="bg-transparent outline-none text-sm text-gray-700"
                        >
                            <option value="all">All</option>
                            <option value="seen">Seen</option>
                            <option value="unseen">Unseen</option>
                        </select>
                    </div>

                    <button
                        onClick={() => setRefreshKey((p) => p + 1)}
                        className="flex items-center gap-2 px-4 py-2 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {historyLoading ? (
                <div className="py-16 flex flex-col items-center justify-center text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-3" />
                    <p className="text-sm text-gray-500">Loading notification history...</p>
                </div>
            ) : groupedItems.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-gray-200 rounded-3xl">
                    <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-700">
                        No notifications found
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        There is no notification history for the selected filter.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {groupedItems.map((item) => {
                        const isOffDuty = item.type === "off_duty";

                        return (
                            <div
                                key={`${item.type}-${item.id}`}
                                className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-all"
                            >
                                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                                    <div className="flex gap-4 min-w-0 flex-1">
                                        {renderTypeIcon(item.type)}

                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <h3 className="text-base font-semibold text-gray-800">
                                                    {item.title}
                                                </h3>

                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${item.type === "off_duty"
                                                            ? "bg-orange-100 text-orange-700"
                                                            : item.type === "kyc"
                                                                ? "bg-amber-100 text-amber-700"
                                                                : "bg-gray-100 text-gray-700"
                                                        }`}
                                                >
                                                    {item.type === "off_duty"
                                                        ? "Off Duty Request"
                                                        : item.type === "kyc"
                                                            ? "KYC Alert"
                                                            : "Notification"}
                                                </span>
                                            </div>

                                            <p className="text-sm leading-6 text-gray-600 whitespace-pre-line">
                                                {item.body}
                                            </p>

                                            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                                                <Clock3 className="w-4 h-4" />
                                                <span>{formatDateTime(item.createdOn)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {isOffDuty && (
                                        <div className="flex flex-wrap gap-2 xl:pl-4">
                                            <button
                                                onClick={() => openActionModal(item, "yes")}
                                                disabled={historyActionLoadingId === item.notificationId}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-all disabled:opacity-60"
                                            >
                                                {historyActionLoadingId === item.notificationId ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <CheckCircle2 className="w-4 h-4" />
                                                )}
                                                Approve
                                            </button>

                                            <button
                                                onClick={() => openActionModal(item, "no")}
                                                disabled={historyActionLoadingId === item.notificationId}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all disabled:opacity-60"
                                            >
                                                {historyActionLoadingId === item.notificationId ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                                Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {actionModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">
                                {actionModal.action === "yes"
                                    ? "Approve Off Duty Request"
                                    : "Reject Off Duty Request"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Add remarks for this action.
                            </p>
                        </div>

                        <div className="p-6">
                            <div className="mb-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <p className="text-sm font-medium text-gray-800 mb-1">
                                    {actionModal.item?.title}
                                </p>
                                <p className="text-sm text-gray-600 whitespace-pre-line">
                                    {actionModal.item?.body}
                                </p>
                            </div>

                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Admin Remarks
                            </label>
                            <textarea
                                rows={4}
                                value={actionModal.remarks}
                                onChange={(e) =>
                                    setActionModal((prev) => ({
                                        ...prev,
                                        remarks: e.target.value,
                                    }))
                                }
                                placeholder="Write remarks here..."
                                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                            />
                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={closeActionModal}
                                className="px-4 py-2 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-all"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSubmitAction}
                                disabled={
                                    historyActionLoadingId === actionModal.item?.notificationId
                                }
                                className={`px-4 py-2 rounded-2xl text-white font-medium transition-all ${actionModal.action === "yes"
                                        ? "bg-emerald-500 hover:bg-emerald-600"
                                        : "bg-red-500 hover:bg-red-600"
                                    } disabled:opacity-60`}
                            >
                                {historyActionLoadingId === actionModal.item?.notificationId ? (
                                    <span className="inline-flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Processing...
                                    </span>
                                ) : actionModal.action === "yes" ? (
                                    "Approve"
                                ) : (
                                    "Reject"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationHistory;